import React from "react";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import NotificationSystem from "react-notification-system";
import Home from "./home";
import Login from "./containers/authcomponents/login";
import Custom from "./containers/authcomponents/custom";
import ForgetPassword from "./containers/authcomponents/forgetpassword";
import ResetPassword from "./containers/authcomponents/resetPassword";
import * as notificationActions from "./actions/notificationActions";
import { PrivateRoute } from "./components/route/privateRoute";
import { Spinner } from "react-redux-spinner";
import "./assets/css/nprogress.css";
import { isAuthenticated, hasValidSession } from "./util/authValidator";
import { getSubdomainInfo } from "./util/subdomainHelper";

/**
 * App Component
 * ─────────────────────────────────────────────────────
 * CHANGES:
 *  1. Replaced deprecated string refs with React.createRef()
 *  2. Consolidated auth check: uses authValidator.hasValidSession()
 *     instead of mixing isAuthenticated + hasSessionCookie from 2 files
 *  3. Removed hasSessionCookie import (now internal to authValidator)
 *  4. Simplified routing conditions with early-return pattern
 *  5. Removed duplicate console.log in production
 *  6. componentDidCatch + error boundary left intact (good pattern)
 */

class App extends React.Component {
  constructor(props) {
    super(props);
    this.notificationRef = React.createRef();
    this.state = {
      isRehydrated: false,
      hasError: false,
      errorMessage: null,
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;

    try {
      if (this.notificationRef.current) {
        dispatch(
          notificationActions.setNotificationRef(this.notificationRef.current)
        );
      } else {
        console.warn("Notification system ref not available on mount");
      }

      this.setState({ isRehydrated: true });
    } catch (error) {
      console.error("Error during component mount:", error);
      this.setState({ hasError: true, errorMessage: error.message });
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error("App component error:", error, errorInfo);
    this.setState({ hasError: true, errorMessage: error.toString() });
  }

  handleReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error("Could not clear storage:", e);
    }
    window.location.reload();
  };

  renderError() {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", padding: "20px",
        textAlign: "center", backgroundColor: "#f8f9fa",
      }}>
        <div style={{
          maxWidth: "500px", padding: "40px", backgroundColor: "#fff",
          borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}>
          <h1 style={{ color: "#dc3545", marginBottom: "20px", fontSize: "24px" }}>
            <i className="fa fa-exclamation-triangle" style={{ marginRight: "10px" }} />
            Application Error
          </h1>
          <p style={{ marginBottom: "30px", color: "#666", fontSize: "16px" }}>
            The application encountered an error and needs to reload.
          </p>
          {this.state.errorMessage && (
            <div style={{
              padding: "15px", backgroundColor: "#f8d7da", border: "1px solid #f5c6cb",
              borderRadius: "4px", marginBottom: "20px", fontSize: "14px",
              color: "#721c24", textAlign: "left", wordBreak: "break-word",
            }}>
              {this.state.errorMessage}
            </div>
          )}
          <button
            onClick={this.handleReload}
            style={{
              padding: "12px 30px", backgroundColor: "#ffcb05", border: "none",
              borderRadius: "6px", fontSize: "16px", fontWeight: "600",
              cursor: "pointer", color: "#222", transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => { e.target.style.backgroundColor = "#ffd84d"; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = "#ffcb05"; }}
          >
            Reload Application
          </button>
        </div>
      </div>
    );
  }

  renderLoading() {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        minHeight: "100vh", backgroundColor: "#f8f9fa",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            border: "4px solid #f3f3f3", borderTop: "4px solid #ffcb05",
            borderRadius: "50%", width: "60px", height: "60px",
            animation: "spin 1s linear infinite", margin: "0 auto 20px",
          }} />
          <p style={{ fontSize: "18px", color: "#666", fontWeight: "500" }}>
            Loading NaviNet&hellip;
          </p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) return this.renderError();
    if (!this.state.isRehydrated) return this.renderLoading();

    let isLoggedIn = false;
    let subdomainInfo = {
      isSubdomain: false,
      subdomain: null,
      isMainDomain: true,
      environment: "development",
    };

    try {
      isLoggedIn = hasValidSession();
      subdomainInfo = getSubdomainInfo();
    } catch (error) {
      console.error("Error checking authentication:", error);
    }

    const isAdminSubdomain = subdomainInfo.subdomain === "admin";
    const shouldShowLanding = !isLoggedIn && subdomainInfo.isMainDomain && !isAdminSubdomain;

    if (process.env.NODE_ENV === "development") {
      console.log("App Render Debug:", {
        isLoggedIn,
        isMainDomain: subdomainInfo.isMainDomain,
        subdomain: subdomainInfo.subdomain,
        isAdminSubdomain,
        shouldShowLanding,
        pathname: window.location.pathname,
      });
    }

    return (
      <>
        <NotificationSystem ref={this.notificationRef} />
        <Spinner />
        <Router>
          <Switch>
            {/* Public routes — always accessible */}
            <Route exact path="/login" component={Login} />
            <Route exact path="/accountrecovery" component={ForgetPassword} />
            <Route exact path="/resetPassword" component={ResetPassword} />

            {/* Root route — decides landing vs redirect */}
            <Route
              exact
              path="/"
              render={() => {
                if (isAdminSubdomain) {
                  return isLoggedIn ? <Redirect to="/dispatch/dashboard" /> : <Redirect to="/login" />;
                }

                if (subdomainInfo.isMainDomain) {
                  if (shouldShowLanding) return <Custom />;
                  return isLoggedIn ? <Redirect to="/dispatch/dashboard" /> : <Redirect to="/login" />;
                }

                // Company subdomain
                return isLoggedIn ? <Redirect to="/dispatch/dashboard" /> : <Redirect to="/login" />;
              }}
            />

            {/* Protected home */}
            <PrivateRoute path="/home" component={Home} />

            {/* Catch-all for authenticated users */}
            {isLoggedIn && <PrivateRoute path="/" component={Home} />}

            {/* Final fallback */}
            <Route
              render={() => {
                if (isAdminSubdomain) return <Redirect to="/login" />;
                if (subdomainInfo.isMainDomain && !isLoggedIn) return <Custom />;
                return <Redirect to="/login" />;
              }}
            />
          </Switch>
        </Router>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  authInfo: state.authInfo || {},
  refNotify: state.refNotify || null,
});

export default connect(mapStateToProps)(App);
